//
// ============================================================================
// IMPORTS
// ============================================================================
//

import type { PendingFiltersDraft } from "../../../services/api/domains/pending/pending.types";
import { convertFiltersToApiFormat, hasActiveFilters } from "../../../utils/filterUtils";

//
// ============================================================================
// TYPES
// ============================================================================
//

interface FilterChip {
  key: string;
  label: string;
  value: number;
  type: keyof PendingFiltersDraft;
  icon: string;
  color: string;
  variant: "tonal" | "outlined";
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Filters Composable
 *
 * Manages sample filtering with draft/applied pattern.
 *
 * Responsibilities:
 * - Maintain draft filters (user's temporary selections)
 * - Maintain applied filters (currently active filters)
 * - Convert filters to API format for backend queries
 * - Generate filter chips for UI display
 * - Load filter options from backend (locations, instruments, etc.)
 * - Apply, cancel, clear, and remove individual filters
 *
 * @param key - Unique key for state isolation (default: "default")
 */
export const useValidationFilters = (key: string = "default") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Base filter options (locations, instruments, doctors, etc.)
  const baseFiltersComposable = useFilters(`${key}.filters`);

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  // Draft filters: User's temporary selections (not yet active)
  const draftFilters = useSessionState<PendingFiltersDraft>(`${key}.draftFilters`, {});

  // Applied filters: Currently active filters (sent to API)
  const appliedFilters = useSessionState<PendingFiltersDraft>(`${key}.appliedFilters`, {});

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  // Convert UI filters to API query format
  const apiFilters = computed(() => convertFiltersToApiFormat(appliedFilters.value));

  // Check if any filters are currently active
  const hasFiltersActive = computed(() => hasActiveFilters(appliedFilters.value));

  /**
   * Generate filter chips for UI display
   *
   * Purpose:
   * - Create visual chips showing active filters
   * - Provide remove functionality via chip click
   * - Show appropriate icons and colors per filter type
   */
  const activeFilterChips = computed((): FilterChip[] => {
    const chips: FilterChip[] = [];
    const { locations, instruments, sampleTypes, visitTypes, requesters, doctors } =
      baseFiltersComposable.filterOptions.value;

    // Helper to create chips
    const addChips = <T extends { id: number | string; name: string }>(
      ids: number[] | undefined,
      items: T[],
      type: keyof PendingFiltersDraft,
      icon: string,
    ) => {
      ids?.forEach((id) => {
        const item = items.find((i) => i.id.toString() === id.toString());
        if (item) {
          chips.push({
            key: `${type}-${id}`,
            label: item.name,
            value: typeof id === "string" ? parseInt(id) : id,
            type,
            icon,
            color: "primary",
            variant: "outlined",
          });
        }
      });
    };

    // Add all filter chips
    addChips(appliedFilters.value.locationIds, [...locations], "locationIds", "mdi-storefront-outline ");
    addChips(appliedFilters.value.instrumentIds, [...instruments], "instrumentIds", "mdi-microscope");
    addChips(appliedFilters.value.sampleTypeIds, [...sampleTypes], "sampleTypeIds", "mdi-test-tube");
    addChips(appliedFilters.value.visitTypeIds, [...visitTypes], "visitTypeIds", "mdi-call-split");
    addChips(appliedFilters.value.requesterIds, [...requesters], "requesterIds", "mdi-hospital-building");

    // Doctor chips (special formatting)
    appliedFilters.value.doctorIds?.forEach((id) => {
      const doctor = doctors.find((d) => d.id.toString() === id.toString());
      if (doctor) {
        chips.push({
          key: `doctor-${id}`,
          label: `${doctor.familyName}, ${doctor.givenName}`,
          value: typeof id === "string" ? parseInt(id) : id,
          type: "doctorIds",
          icon: "mdi-stethoscope",
          color: "primary",
          variant: "outlined",
        });
      }
    });

    // Priority chip
    if (appliedFilters.value.stat) {
      chips.push({
        key: "priority",
        label: "Priority (STAT)",
        value: 0,
        type: "stat" as any,
        icon: "mdi-bell-check-outline",
        color: "primary",
        variant: "outlined",
      });
    }

    return chips;
  });

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Apply draft filters to active filters
   *
   * Purpose:
   * - Copy user's temporary selections to active state
   * - Trigger sample refetch with new criteria
   */
  const applyFilters = () => {
    appliedFilters.value = { ...draftFilters.value };
  };

  /**
   * Cancel filter changes and restore previous state
   *
   * Purpose:
   * - Discard user's temporary selections
   * - Restore draft to currently active filters
   */
  const cancelFilters = () => {
    draftFilters.value = { ...appliedFilters.value };
  };

  /**
   * Clear all filters (both draft and applied)
   *
   * Purpose:
   * - Remove all active filters
   * - Reset to default "show all" view
   */
  const clearFilters = () => {
    appliedFilters.value = {};
    draftFilters.value = {};
  };

  /**
   * Remove a specific filter by type and value
   *
   * Purpose:
   * - Remove individual filter from both draft and applied
   * - Triggered by clicking "X" on filter chip
   *
   * @param type - Filter type (locationIds, doctorIds, stat, etc.)
   * @param value - Filter value to remove
   */
  const removeFilter = (type: keyof PendingFiltersDraft, value: number) => {
    if (type === "stat") {
      delete appliedFilters.value.stat;
      delete draftFilters.value.stat;
    } else {
      const appliedValues = appliedFilters.value[type] as number[] | undefined;
      if (appliedValues) {
        appliedFilters.value[type] = appliedValues.filter((v) => v !== value) as any;
      }

      const draftValues = draftFilters.value[type] as number[] | undefined;
      if (draftValues) {
        draftFilters.value[type] = draftValues.filter((v) => v !== value) as any;
      }
    }
  };

  /**
   * Load filter options from backend (if not already loaded)
   *
   * Purpose:
   * - Fetch locations, instruments, doctors, etc.
   * - Load once and cache for session
   * - Called on page init or filter dialog open
   */
  const loadFilterOptions = async () => {
    if (baseFiltersComposable.filterOptions.value.locations.length === 0) {
      await baseFiltersComposable.fetchAllFilters();
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // Composable
    baseFiltersComposable,

    // State
    draftFilters,
    appliedFilters,
    filterOptions: baseFiltersComposable.filterOptions,
    filtersLoading: baseFiltersComposable.loading,

    // Computed
    apiFilters,
    hasFiltersActive,
    activeFilterChips,

    // Methods
    applyFilters,
    cancelFilters,
    clearFilters,
    removeFilter,
    loadFilterOptions,
  };
};
